// Booking Dispute Statement — system-generated evidence for Owner/Admin
import jsPDF from 'jspdf';
import {
  BRAND, rgb, fmtINR, fmtDate, fmtDateTime, computeDocHash,
  roundRectFS, drawDiamond, drawStatusPill,
} from './shared';
import { formatHoursHm } from '../durationHours';

const COMPANY = {
  name: 'DEVELOPMENT EXPRESS',
  legal: 'Development Express Pvt Ltd',
  addr: 'Karad, Satara, Maharashtra - 415110',
  phone: '+91-8408000084',
  email: 'om.chavan2026@zohomail.in',
  web: 'developmentexpress.in',
};

const MARGIN = { top: 25, bottom: 28, left: 15, right: 15 };
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN.left - MARGIN.right; // 180mm
const HEADER_H = 42;

function machineLabel(booking) {
  const m = Array.isArray(booking.machines) ? booking.machines[0] : booking.machines;
  return m?.name || m?.machine_id || booking.machineName || booking.machine_id || '—';
}

function operatorLabel(booking) {
  const op = booking.operator;
  if (op && typeof op === 'object') return op.name || '—';
  if (typeof op === 'string' && op.trim()) return op;
  return booking.operatorName || '—';
}

function workSiteLabel(booking) {
  return (
    String(booking.site_address || '').trim()
    || String(booking.site_location || '').trim()
    || String(booking.location || '').trim()
    || '—'
  );
}

function estimateTotal(booking) {
  const gross = Number(booking.gross_amount_final || 0);
  if (gross > 0) return gross;
  const total = Number(booking.total_amount || 0);
  if (total > 0) return total;
  const hours = Number(
    booking.operator_actual_hours
    ?? booking.admin_override_hours
    ?? booking.booked_hours
    ?? 0,
  );
  const rate = Number(booking.rate_per_hour || 0);
  if (hours > 0 && rate > 0) {
    const base = Math.round(hours * rate * 100) / 100;
    const origBase = Number(booking.base_amount || 0);
    const origGst = Number(booking.gst_amount || 0);
    const gstRate = origBase > 0 ? origGst / origBase : 0.18;
    return Math.round((base + base * gstRate) * 100) / 100;
  }
  const base = Number(booking.base_amount || 0);
  const gst = Number(booking.gst_amount || 0);
  return Math.round((base + gst) * 100) / 100;
}

function drawSectionTitle(doc, title, y, contentW) {
  doc.setFillColor(...rgb(BRAND.GOLD_PALE));
  doc.rect(MARGIN.left, y, contentW, 7, 'F');
  doc.setDrawColor(...rgb(BRAND.GOLD));
  doc.setLineWidth(0.2);
  doc.line(MARGIN.left, y + 7, MARGIN.left + contentW, y + 7);
  doc.setTextColor(...rgb(BRAND.GOLD_DARK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(title.toUpperCase(), MARGIN.left + 3, y + 4.8);
  return y + 12;
}

function drawKV(doc, label, value, x, y, labelW = 48, valueW = 120) {
  doc.setTextColor(...rgb(BRAND.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(label, x, y);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const lines = doc.splitTextToSize(String(value || '—'), valueW);
  doc.text(lines, x + labelW, y);
  return y + Math.max(5.5, lines.length * 4.2);
}

/**
 * @param {object} booking - Disputed booking row (API / dashboard shape)
 * @param {{ download?: boolean }} options
 */
export async function generateDisputeStatement(booking = {}, options = {}) {
  const { download = true } = options;

  const bookingRef = booking.booking_ref || booking.id || '—';
  const generatedAt = new Date().toISOString();
  const total = estimateTotal(booking);
  const advance = Number(booking.advance_paid || booking.advancePaid || 0);
  const outstanding = Math.max(0, Math.round((total - advance) * 100) / 100);
  const hours = Number(
    booking.operator_actual_hours
    ?? booking.admin_override_hours
    ?? '—',
  );
  const workStarted = booking.machine_started_at
    ? fmtDateTime(booking.machine_started_at)
    : '—';
  const completedAt = booking.operator_completed_at
    ? fmtDateTime(booking.operator_completed_at)
    : '—';
  const disputeRaised = booking.dispute_raised_at
    ? fmtDateTime(booking.dispute_raised_at)
    : '—';
  const disputeReason = String(booking.dispute_reason || '—').trim() || '—';
  const workDescription = String(booking.work_description || '').trim() || '—';
  const docHash = await computeDocHash(bookingRef, generatedAt, outstanding);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth() || PAGE_W;
  const contentW = (w - MARGIN.left - MARGIN.right) || CONTENT_W;

  const drawHeaderBand = () => {
    doc.setFillColor(...rgb(BRAND.COVER_BG));
    doc.rect(0, 0, w, HEADER_H, 'F');
    doc.setFillColor(...rgb(BRAND.GOLD));
    doc.rect(0, HEADER_H, w, 1.2, 'F');

    doc.setTextColor(...rgb(BRAND.COVER_GOLD));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(COMPANY.name, MARGIN.left, 16);

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Booking Dispute Statement', MARGIN.left, 26);

    doc.setTextColor(...rgb(BRAND.COVER_GOLD_LT));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Generated: ${fmtDateTime(generatedAt)}`, MARGIN.left, 34);

    drawStatusPill(doc, w - MARGIN.right - 28, 14, 28, 7, 'DISPUTED', BRAND.RUBY_LT, BRAND.RUBY);
  };

  const drawFooterBand = () => {
    const pageH = doc.internal.pageSize.getHeight() || PAGE_H;
    doc.setDrawColor(...rgb(BRAND.GOLD));
    doc.setLineWidth(0.4);
    doc.line(MARGIN.left, pageH - 22, w / 2 - 4, pageH - 22);
    doc.line(w / 2 + 4, pageH - 22, w - MARGIN.right, pageH - 22);
    drawDiamond(doc, w / 2, pageH - 22, 1.3, BRAND.GOLD);

    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(COMPANY.legal, w / 2, pageH - 15, { align: 'center' });
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(
      'This document is system-generated evidence for dispute resolution.',
      w / 2,
      pageH - 10,
      { align: 'center' },
    );
    doc.setFontSize(5.5);
    doc.setTextColor(...rgb(BRAND.MUTED_LT));
    doc.text(`${COMPANY.web}  ·  ${COMPANY.email}  ·  ${COMPANY.phone}`, w / 2, pageH - 6, { align: 'center' });
    doc.text(`Audit: ${docHash.substring(0, 16)}  ·  ${fmtDate(generatedAt)}`, w / 2, pageH - 2.5, { align: 'center' });
  };

  drawHeaderBand();

  let y = HEADER_H + 10;

  const ensureSpace = (neededMm) => {
    const pageH = doc.internal.pageSize.getHeight() || PAGE_H;
    if (y + neededMm > pageH - MARGIN.bottom) {
      drawFooterBand();
      doc.addPage();
      drawHeaderBand();
      y = HEADER_H + 10;
    }
  };

  // 2. Booking Details
  ensureSpace(40);
  y = drawSectionTitle(doc, '1. Booking Details', y, contentW);
  y = drawKV(doc, 'Booking Ref', bookingRef, MARGIN.left + 3, y);
  y = drawKV(doc, 'Status', 'Disputed', MARGIN.left + 3, y);
  y = drawKV(doc, 'Machine', machineLabel(booking), MARGIN.left + 3, y);
  y = drawKV(doc, 'Operator', operatorLabel(booking), MARGIN.left + 3, y);
  y = drawKV(doc, 'Work Site', workSiteLabel(booking), MARGIN.left + 3, y);
  y += 4;

  // 3. Work Summary
  ensureSpace(40);
  y = drawSectionTitle(doc, '2. Work Summary', y, contentW);
  y = drawKV(doc, 'Work Started', workStarted, MARGIN.left + 3, y);
  y = drawKV(doc, 'Operator Marked Complete', completedAt, MARGIN.left + 3, y, 58, 110);
  y = drawKV(doc, 'Hours Worked', Number.isFinite(hours) ? formatHoursHm(hours) : String(hours), MARGIN.left + 3, y);
  y = drawKV(doc, 'Work Description', workDescription, MARGIN.left + 3, y, 48, contentW - 55);
  y += 4;

  // 4. Payment Details
  ensureSpace(40);
  y = drawSectionTitle(doc, '3. Payment Details', y, contentW);
  const cardW = (contentW - 8) / 3;
  [
    { label: 'TOTAL AMOUNT', value: fmtINR(total), accent: BRAND.GOLD },
    { label: 'ADVANCE PAID', value: fmtINR(advance), accent: BRAND.EMERALD },
    { label: 'OUTSTANDING (UNPAID)', value: fmtINR(outstanding), accent: BRAND.RUBY },
  ].forEach((card, i) => {
    const x = MARGIN.left + i * (cardW + 4);
    roundRectFS(doc, x, y, cardW, 18, 1.5, '#FFFFFF', BRAND.BORDER, 0.25);
    doc.setFillColor(...rgb(card.accent));
    doc.rect(x, y, cardW, 1.2, 'F');
    doc.setTextColor(...rgb(BRAND.MUTED));
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.text(card.label, x + 3, y + 6);
    doc.setTextColor(...rgb(BRAND.INK));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(card.value, x + 3, y + 13);
  });
  y += 24;

  // 5. Dispute Details
  ensureSpace(30);
  y = drawSectionTitle(doc, '4. Dispute Details', y, contentW);
  y = drawKV(doc, 'Client Dispute Reason', disputeReason, MARGIN.left + 3, y, 52, contentW - 58);
  y = drawKV(doc, 'Dispute Raised', disputeRaised, MARGIN.left + 3, y);
  y += 4;

  // 6. Legal Note
  ensureSpace(35);
  y = drawSectionTitle(doc, '5. Legal Note', y, contentW);
  roundRectFS(doc, MARGIN.left, y, contentW, 22, 1.5, BRAND.GOLD_PALE, BRAND.GOLD, 0.3);
  doc.setTextColor(...rgb(BRAND.INK));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  const legalLines = doc.splitTextToSize(
    'Payment obligations remain subject to the accepted Terms & Conditions and final dispute resolution. Jurisdiction: Karad, Satara, Maharashtra.',
    contentW - 10,
  );
  doc.text(legalLines, MARGIN.left + 3, y + 7);

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooterBand();
  }

  const safeId = String(bookingRef).replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 40);
  const filename = `DE_Dispute_Statement_${safeId}.pdf`;
  if (download) doc.save(filename);
  doc.__deFilename = filename;
  return doc;
}
